/**
 * @fileoverview User slice for tier management using Zustand
 */

import type { StateCreator } from 'zustand';
import type { UserSlice, UserTier, UserProfile } from '../../types/user';
import { apiClient } from '../../services/api/client';

// Import the tier features configuration
const TIER_FEATURES_CONFIG = {
  free: {
    maxCustomStrategies: 1,
    maxSavedBacktests: 5,
    canShareStrategies: false,
    canAccessCommunityStrategies: false,
    accessToBuiltInStrategies: 'all' as const,
    advancedAnalytics: false,
    walkForwardAnalysis: false,
    prioritySupport: false,
    exportData: false,
    apiAccess: 'none' as const,
    creditsPerMonth: 50000,
    maxConcurrentJobs: 1,
  },
  independent: {
    maxCustomStrategies: 5,
    maxSavedBacktests: -1,
    canShareStrategies: false,
    canAccessCommunityStrategies: false,
    accessToBuiltInStrategies: 'all' as const,
    advancedAnalytics: true,
    walkForwardAnalysis: true,
    prioritySupport: false,
    exportData: false,
    apiAccess: 'none' as const,
    creditsPerMonth: 300000,
    maxConcurrentJobs: 1,
  },
  professional: {
    maxCustomStrategies: 20,
    maxSavedBacktests: -1,
    canShareStrategies: true,
    canAccessCommunityStrategies: true,
    accessToBuiltInStrategies: 'all' as const,
    advancedAnalytics: true,
    walkForwardAnalysis: true,
    prioritySupport: true,
    exportData: false,
    apiAccess: 'read' as const,
    creditsPerMonth: 1200000,
    maxConcurrentJobs: 2,
  },
  advanced: {
    maxCustomStrategies: -1, // unlimited
    maxSavedBacktests: -1, // unlimited
    canShareStrategies: true,
    canAccessCommunityStrategies: true,
    accessToBuiltInStrategies: 'all' as const,
    advancedAnalytics: true,
    walkForwardAnalysis: true,
    prioritySupport: true,
    exportData: true,
    apiAccess: 'full' as const,
    creditsPerMonth: 3000000,
    maxConcurrentJobs: 4,
  }
} as const;

export const createUserSlice: StateCreator<UserSlice> = (set, get) => ({
  // Initial state — restore cached tier to avoid flash on refresh
  profile: null,
  isLoading: false,
  error: null,
  tier: (localStorage.getItem('cachedUserTier') as UserTier) || null,
  limits: TIER_FEATURES_CONFIG[(localStorage.getItem('cachedUserTier') as UserTier)] || TIER_FEATURES_CONFIG.free,
  usage: {
    strategiesCreated: 0,
    savedBacktests: 0
  },

  // Actions
  setUserProfile: (profile: UserProfile) => {
    set({
      profile,
      tier: profile.tier,
      limits: TIER_FEATURES_CONFIG[profile.tier as UserTier] || TIER_FEATURES_CONFIG.free,
      usage: {
        strategiesCreated: profile.strategiesCreated,
        savedBacktests: profile.savedBacktests
      },
      isLoading: false,
      error: null
    });
  },

  setUserLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setUserError: (error: string | null) => {
    set({ error, isLoading: false });
  },

  clearUser: () => {
    localStorage.removeItem('cachedUserTier');
    set({
      profile: null,
      tier: null,
      limits: TIER_FEATURES_CONFIG.free,
      usage: {
        strategiesCreated: 0,
        savedBacktests: 0
      },
      error: null,
      isLoading: false
    });
  },

  setUserTier: (tier: UserTier | null) => {
    set({
      tier,
      limits: tier ? TIER_FEATURES_CONFIG[tier] : TIER_FEATURES_CONFIG.free
    });
  },

  updateUsage: (usage) => {
    const currentUsage = get().usage;
    set({
      usage: {
        ...currentUsage,
        ...usage
      }
    });
  },

  incrementStrategyCount: async () => {
    // Instead of incrementing a counter, refresh the actual usage from the database
    await get().refreshUserData();
  },

  incrementBacktestCount: async () => {
    // Instead of incrementing a counter, refresh the actual usage from the database
    await get().refreshUserData();
  },

  canCreateStrategy: () => {
    const { limits, usage } = get();
    const safeLimits = limits || TIER_FEATURES_CONFIG.free;
    return safeLimits.maxCustomStrategies === -1 || usage.strategiesCreated < safeLimits.maxCustomStrategies;
  },

  canSaveBacktest: () => {
    const { limits, usage } = get();
    const safeLimits = limits || TIER_FEATURES_CONFIG.free;
    return safeLimits.maxSavedBacktests === -1 || usage.savedBacktests < safeLimits.maxSavedBacktests;
  },

  canShareStrategies: () => {
    const { limits } = get();
    const safeLimits = limits || TIER_FEATURES_CONFIG.free;
    return safeLimits.canShareStrategies;
  },

  fetchUserProfile: async () => {
    // Alias for refreshUserData for backwards compatibility
    await get().refreshUserData();
  },

  refreshUserData: async () => {
    try {
      set({ isLoading: true, error: null });
      
      const profileTimeout = { timeout: 15000, retry: false };
      
      // Fetch profile and tier usage in parallel with short timeout, no retries
      const [response, tierUsageResponse] = await Promise.all([
        apiClient.get<{success: boolean; data: UserProfile}>('/user/profile', profileTimeout),
        apiClient.get<{
          success: boolean;
          data: {
            tier: UserTier;
            limits: {
              maxStrategies: number;
              maxBacktests: number;
              maxOptimizationJobs?: number;
            };
            usage: {
              strategiesCreated: number;
              savedBacktests: number;
            };
          }
        }>('/user/tier-usage', profileTimeout),
      ]);
      
      const profile = response.data;
      const tierUsage = tierUsageResponse.data;
      
      // Cache tier in localStorage so next page load starts with correct tier
      if (tierUsage.tier) {
        localStorage.setItem('cachedUserTier', tierUsage.tier);
      } else {
        localStorage.removeItem('cachedUserTier');
      }
      
      set({
        profile,
        tier: tierUsage.tier,
        limits: TIER_FEATURES_CONFIG[tierUsage.tier as UserTier] || TIER_FEATURES_CONFIG.free,
        usage: tierUsage.usage,
        isLoading: false,
        error: null
      });
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to refresh user data'
      });
    }
  }
});
